import { inject, Injectable, InjectionToken } from '@angular/core';

/**
 * Auth lifecycle telemetry (FR-016, R-111) — a closed, tenant-free event
 * schema modeled on the AITH-31 RealtimeTelemetry discipline. The interface
 * has no field capable of carrying identity, claims, credentials, tenant
 * values, URLs, or raw errors; SC-006's 0% requirement is enforced by this
 * type surface plus the allowlist unit test, not by reviewer vigilance.
 */

export type AuthEventName =
  | 'signIn'                 // FR-016: sign-in completed
  | 'signInFailure'          // FR-019: provider error or user cancellation
  | 'signOut'                // FR-014
  | 'renewal'                // FR-013: silent renewal attempt resolved
  | 'denial'                 // FR-012: not provisioned (role/tenant)
  | 'unauthorizedNavigation';// FR-011: role-refused navigation

export type AuthIssuerCategory = 'staff' | 'client';
export type AuthOutcome = 'success' | 'failure';

export interface AuthEvent {
  readonly event: AuthEventName;
  readonly issuerCategory: AuthIssuerCategory;
  readonly outcome: AuthOutcome;
  /** Random per auth lifecycle; meaningless outside the browser. */
  readonly correlationId: string;
}

export abstract class AuthTelemetrySink {
  abstract emit(record: AuthEvent): void | Promise<void>;
}

export const AUTH_TELEMETRY_SINK = new InjectionToken<AuthTelemetrySink>(
  'AUTH_TELEMETRY_SINK',
);

export const AUTH_CORRELATION_ID_FACTORY = new InjectionToken<() => string>(
  'AUTH_CORRELATION_ID_FACTORY',
  { factory: () => () => crypto.randomUUID() },
);

@Injectable()
export class AuthTelemetry {
  private readonly sink = inject(AUTH_TELEMETRY_SINK);
  private readonly correlationId = inject(AUTH_CORRELATION_ID_FACTORY)();

  signIn(issuerCategory: AuthIssuerCategory): void {
    this.emit({ event: 'signIn', issuerCategory, outcome: 'success' });
  }

  signInFailure(issuerCategory: AuthIssuerCategory): void {
    this.emit({ event: 'signInFailure', issuerCategory, outcome: 'failure' });
  }

  signOut(issuerCategory: AuthIssuerCategory): void {
    this.emit({ event: 'signOut', issuerCategory, outcome: 'success' });
  }

  /** Recorded once per terminal renewal failure, not per internal retry. */
  renewal(issuerCategory: AuthIssuerCategory, outcome: AuthOutcome): void {
    this.emit({ event: 'renewal', issuerCategory, outcome });
  }

  /** Not provisioned (FR-012) — always a failure outcome. */
  denial(issuerCategory: AuthIssuerCategory): void {
    this.emit({ event: 'denial', issuerCategory, outcome: 'failure' });
  }

  /** Role-refused navigation (FR-011) — always a failure outcome. */
  unauthorizedNavigation(issuerCategory: AuthIssuerCategory): void {
    this.emit({ event: 'unauthorizedNavigation', issuerCategory, outcome: 'failure' });
  }

  private emit(record: Omit<AuthEvent, 'correlationId'>): void {
    const frozenRecord: AuthEvent = Object.freeze({
      ...record,
      correlationId: this.correlationId,
    });
    try {
      const result = this.sink.emit(frozenRecord);
      if (result instanceof Promise) void result.catch(() => undefined);
    } catch {
      // Telemetry is deliberately isolated from the auth lifecycle: a sink
      // failure can never alter an auth outcome (contract rule 4).
    }
  }
}

export class ConsoleAuthTelemetrySink extends AuthTelemetrySink {
  override emit(record: AuthEvent): void {
    console.info('[auth]', record);
  }
}
